import type { useChat } from '@ai-sdk/react';
import type { JSONValue } from 'ai';
import { updateMessages } from './update-messages';
import type { StripeIntegration } from '~/lib/.server/llm/providers/types';
import { BOLT_PROMPT_PATH } from '~/lib/constants';
import { settingsService } from '~/lib/services/settings-service';
import { chatStore } from '~/lib/stores/chat';
import { projectStore } from '~/lib/stores/project';
import { featurePreviewsStore } from '~/lib/stores/settings';
import { stripeStore } from '~/lib/stores/stripe';
import { supabaseStore } from '~/lib/stores/supabase';
import { userStore } from '~/lib/stores/user';
import { workbenchStore } from '~/lib/stores/workbench';
import type { Message } from '~/types/chat';
import type { FeaturePreviews } from '~/types/feature-previews';
import { fileMapToArray } from '~/utils/import-files';

type PrepareRequestBodyOptions = Omit<
  Parameters<NonNullable<NonNullable<Parameters<typeof useChat>[0]>['experimental_prepareRequestBody']>>[0],
  'id'
> & { id?: string };

type SerializedMessage = Pick<
  Message,
  'id' | 'content' | 'role' | 'data' | 'annotations' | 'toolInvocations' | 'parts' | 'experimental_attachments'
> & {
  rawContent?: string;
  cache: boolean;
  stripeIntegration?: StripeIntegration;
};

interface RequestBodyTyped {
  messages: SerializedMessage[];
  featurePreviews: Partial<FeaturePreviews>;
}

interface ResponseKind {
  default: JSONValue;
  typed: RequestBodyTyped;
}

export function prepareRequestBody<K extends keyof ResponseKind = 'default'>(
  options: PrepareRequestBodyOptions,
): ResponseKind[K] {
  const currentFiles = workbenchStore.files.get();
  const projectFiles = fileMapToArray(currentFiles);
  const framework = workbenchStore.framework.get()?.name;
  const projectId = projectStore.projectId.get();
  const supabaseProjectId = supabaseStore.supabaseProjectIntegration.get()?.project.id;
  const hasEdgeFunctions = stripeStore.hasEdgeFunctions.get();
  const products = stripeStore.products.get();
  const promptMode = chatStore.promptMode.get();
  const runningCommands = workbenchStore.runningCommands.map(({ command }) => command);
  const stripeConfigured = stripeStore.isConfigured.get();
  const supportIntegrations = userStore.getActiveOrganization().get()?.organizationSettings?.showIntegrations ?? true;
  const globalSystemPrompt = settingsService.readValue('knowledge', 'systemPrompt');
  const projectPrompt = getProjectPrompt();
  const allPackageJsonFiles = workbenchStore.allPackageJsonFiles.get();
  const hostingProvider = projectStore.integrations.get()?.hosting?.data?.hostingProvider;

  const dependencies = userStore.getActiveOrganization().get()
    ? Array.from(
        new Set(
          allPackageJsonFiles.flatMap(({ content }) => [
            ...Object.keys(content?.dependencies ?? {}),
            ...Object.keys(content?.devDependencies ?? {}),
            ...Object.keys(content?.peerDependencies ?? {}),
            ...Object.keys(content?.optionalDependencies ?? {}),
          ]),
        ),
      )
    : [];

  const featurePreviews = {
    // we need to spread to avoid type errors and to satisfy the `JSONValue` type (╥﹏╥)
    ...featurePreviewsStore.get(),
  };

  return {
    ...updateMessages({
      currentFiles,
      projectFiles,
      featurePreviews,
      framework,
      promptMode,
      projectId,
      supabaseProjectId,
      hasEdgeFunctions,
      products,
      supportIntegrations,
      runningCommands,
      stripeConfigured,
      globalSystemPrompt,
      projectPrompt,
      dependencies,
      hostingProvider,
      ...options,
    }),
    id: options.id,
    ...options.requestBody,
  } as ResponseKind[K];
}

function getProjectPrompt() {
  try {
    return workbenchStore.readFile(BOLT_PROMPT_PATH)?.content;
  } catch {
    // ignore errors from non-existing file reads
    return undefined;
  }
}
